/**
 * RouteGuard - Komponent do ochrony tras na podstawie subskrypcji
 *
 * Używa ROUTE_PROTECTION z subscriptionConfig.js do sprawdzenia
 * czy użytkownik ma dostęp do danej ścieżki.
 *
 * Użycie:
 * <RouteGuard>
 *   <PrivateRoute roles={["admin"]}>
 *     <Equipment />
 *   </PrivateRoute>
 * </RouteGuard>
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionUnavailable from './SubscriptionUnavailable';

const RouteGuard = ({ children }) => {
  const location = useLocation();
  const { loading, canAccessRoute, hasAnySubscription, unavailable } = useSubscription();

  // Podczas ładowania pokazuj spinner
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking access...</p>
        </div>
      </div>
    );
  }

  if (unavailable) return <SubscriptionUnavailable />;

  // Sprawdź czy użytkownik ma dostęp do tej trasy
  const hasAccess = canAccessRoute(location.pathname);

  if (!hasAccess) {
    console.warn(`[RouteGuard] Access denied to ${location.pathname} - missing subscription`);

    // Jeśli brak jakiejkolwiek subskrypcji - przekieruj do settings
    if (!hasAnySubscription()) {
      return <Navigate to="/settings" state={{
        from: location.pathname,
        message: 'Your company does not have an active subscription. Please contact your administrator.'
      }} replace />;
    }

    // Jeśli ma subskrypcje ale nie na tę funkcję - przekieruj do admin
    return <Navigate to="/admin" state={{
      from: location.pathname,
      message: 'You do not have access to this feature. Please upgrade your subscription.'
    }} replace />;
  }

  return children;
};

export default RouteGuard;
